/**
 * A short-lived cache for AI enrichment results, keyed by (entity, inputs).
 *
 * WHY THIS EXISTS
 * An enrichment run costs real money — a 200-ref batch with images is several
 * dollars of Anthropic spend — and the results live only in the running tool's
 * React state. Anything that interrupts the review therefore bills the admin
 * twice for the same answers: a dismissed dialog, a mis-clicked close, a mobile
 * tab the OS discards, a reload. This cache makes the second run free.
 *
 * WHERE IT LIVES, AND WHY NOT FIRESTORE
 * A module-level Map, mirrored into `sessionStorage`. The Map survives the
 * dialog unmounting — which is the actual reported failure — and the mirror
 * survives a reload or a discarded tab. Firestore was rejected: this repo does
 * not hold `firestore.rules` (they live in the watchlock repo), so a
 * client-written cache collection would need a cross-repo rules change, plus a
 * TTL policy and a cleanup job, to buy cross-device sharing that a single admin
 * working one list for five minutes does not need. Revisit if that changes.
 *
 * WHAT IT IS NOT
 * Not a correctness mechanism. Every entry expires after `ENRICH_CACHE_TTL_MS`,
 * and any change to the inputs produces a different key, so a stale answer can
 * only be served for a few minutes to inputs that have not moved. Callers that
 * want a guaranteed-fresh answer bypass it explicitly (the per-row Retry).
 *
 * Generic in the value type so the calibre / winder / module enrichment tools
 * can adopt it without changing anything here.
 */

/** How long an entry may be served. Deliberately short — see "not correctness". */
export const ENRICH_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Bump by hand whenever the SHAPE of a cached value changes, or when a prompt or
 * model change should invalidate everything in flight.
 *
 * The client cannot see which model the `enrichReference` callable actually
 * used — the callable picks it server-side from config and doesn't echo it — so
 * the key cannot include a true model version. Over a five-minute window that
 * gap is close to theoretical: a config change would have to land mid-review,
 * and the worst case is one stale row that Retry clears. If cross-device
 * caching ever becomes a real need, having the callable return its model is the
 * change that makes this exact.
 *
 * Bumped to 2 when every enrichReference caller moved to one stored-ref payload
 * builder and the callable began enforcing its allowlist: a result cached from
 * the old payload was produced from different input.
 */
export const ENRICH_CACHE_SCHEMA_VERSION = 2;

/** Cap on retained entries. Bounds both memory and the sessionStorage mirror. */
const MAX_ENTRIES = 300;

const STORAGE_KEY = `wk.admin.enrichCache.v${ENRICH_CACHE_SCHEMA_VERSION}`;

/** Delay before mirroring to sessionStorage, so a 200-ref run doesn't
 *  re-serialise the whole map 200 times. */
const FLUSH_DELAY_MS = 250;

interface StoredEntry {
  value: unknown;
  cachedAt: number;
}

export interface CachedEnrichEntry<T> {
  value: T;
  /** Epoch ms the underlying call completed. Drives the "cached 2m ago" label. */
  cachedAt: number;
}

/**
 * Insertion order doubles as recency: a read moves its key to the end, so the
 * oldest-used entry is always `keys().next()`. Map preserves insertion order,
 * which is the whole trick — no separate LRU bookkeeping.
 */
const memory = new Map<string, StoredEntry>();

let hydrated = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** sessionStorage is absent in tests/SSR and throws outright in some privacy
 *  modes. Every access is guarded; losing the mirror degrades to a memory-only
 *  cache, which is still the fix for the reported failure. */
function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  const store = storage();
  if (!store) return;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    const now = Date.now();
    for (const [key, entry] of Object.entries(parsed as Record<string, StoredEntry>)) {
      // Drop anything already expired rather than carrying it in to be
      // rejected one read later.
      if (!entry || typeof entry.cachedAt !== 'number') continue;
      if (now - entry.cachedAt >= ENRICH_CACHE_TTL_MS) continue;
      memory.set(key, entry);
    }
  } catch (err) {
    console.warn('[enrichResultCache] could not read the session mirror', err);
  }
}

/**
 * Write the memory map through to sessionStorage now.
 *
 * Exported because it is genuinely useful (flush before a deliberate navigation)
 * and because tests need it without waiting on a timer.
 */
export function flushEnrichCache(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const store = storage();
  if (!store) return;
  const write = (): boolean => {
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(memory)));
      return true;
    } catch {
      return false;
    }
  };
  if (write()) return;
  // Almost certainly a quota error. Halve the cache — oldest first — and try
  // once more; if it still fails, carry on with the memory cache alone.
  const half = Math.floor(memory.size / 2);
  let dropped = 0;
  for (const key of memory.keys()) {
    if (dropped >= half) break;
    memory.delete(key);
    dropped += 1;
  }
  if (!write()) {
    try {
      store.removeItem(STORAGE_KEY);
    } catch {
      /* nothing further to try */
    }
    console.warn('[enrichResultCache] session mirror unavailable; memory only');
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEnrichCache();
  }, FLUSH_DELAY_MS);
}

// ── key building ────────────────────────────────────────────────────────────

/**
 * JSON with object keys sorted at every depth, so two structurally-equal inputs
 * always produce the same string. `JSON.stringify` alone does not: it preserves
 * insertion order, and `{a,b}` vs `{b,a}` would key differently and miss.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    // `undefined` members are dropped by JSON.stringify, so dropping them here
    // too keeps { a: 1 } and { a: 1, b: undefined } on the same key.
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

/** FNV-1a, 32-bit. Not cryptographic — this only has to separate inputs. */
function hash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * Build the cache key for one entity's enrichment.
 *
 * `inputs` must describe everything that could change the answer, and must be
 * computable WITHOUT doing the expensive work — key on a product URL rather
 * than its scraped text, on image ids rather than fetched bytes. Otherwise a
 * cache hit still pays for the scrape and the storage round-trips it was meant
 * to skip. The five-minute TTL is what makes that safe.
 */
export function buildEnrichCacheKey(parts: {
  /** Entity family, so two tools can't collide on the same id. */
  kind: string;
  /** Document id of the thing being enriched. */
  id: string;
  /** Everything else that determines the answer. */
  inputs: unknown;
}): string {
  return `${ENRICH_CACHE_SCHEMA_VERSION}:${parts.kind}:${parts.id}:${hash(
    stableStringify(parts.inputs),
  )}`;
}

// ── read / write ────────────────────────────────────────────────────────────

/** The cached value for `key`, or null when absent or expired. */
export function readEnrichCache<T>(key: string, now = Date.now()): CachedEnrichEntry<T> | null {
  hydrate();
  const entry = memory.get(key);
  if (!entry) return null;
  if (now - entry.cachedAt >= ENRICH_CACHE_TTL_MS) {
    memory.delete(key);
    scheduleFlush();
    return null;
  }
  // Touch for recency so the eviction below sheds genuinely cold entries.
  memory.delete(key);
  memory.set(key, entry);
  return { value: entry.value as T, cachedAt: entry.cachedAt };
}

export function writeEnrichCache<T>(key: string, value: T, now = Date.now()): void {
  hydrate();
  memory.delete(key);
  memory.set(key, { value, cachedAt: now });
  while (memory.size > MAX_ENTRIES) {
    const oldest = memory.keys().next();
    if (oldest.done) break;
    memory.delete(oldest.value);
  }
  scheduleFlush();
}

/** Forget one entry — what a "retry, ignore the cache" affordance calls. */
export function invalidateEnrichCache(key: string): void {
  hydrate();
  if (memory.delete(key)) scheduleFlush();
}

/** Drop everything, including the mirror. */
export function clearEnrichCache(): void {
  hydrated = true;
  memory.clear();
  flushEnrichCache();
}

/** Live entries, expired ones excluded. For "N results cached" readouts. */
export function enrichCacheSize(now = Date.now()): number {
  hydrate();
  let n = 0;
  for (const entry of memory.values()) {
    if (now - entry.cachedAt < ENRICH_CACHE_TTL_MS) n += 1;
  }
  return n;
}
